import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, from, lastValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PushNotificationService } from 'src/app/core/push/push-notification.service';
import {
  OFFLINE_SESSION_ENABLED,
  browserReportsOffline,
  clearLogoutPending,
  forgetOfflineIdentity,
  isAuthRejection,
  isLogoutPending,
  readOfflineIdentity,
  rememberOfflineIdentity,
} from 'src/app/core/offline/offline-identity';
import { OfflineModeState } from 'src/app/core/offline/offline-mode.state';
import { canUseOfflineCatalog, isOfflineAllowedFor } from 'src/app/core/offline/offline-routes';
import { State } from 'src/app/store/auth/auth.reducers';
import { BaseAction, SessionActionType } from 'src/app/store/store.actions';
import { EMPTY_AUTH } from '../models/auth';
import { buildAuthToken, decodeAuthToken } from '../utils/jwt-to-auth';
import { isRestorableAppUrl } from '../utils/return-url';
import { purgeLegacyAuthStorage } from '../utils/legacy-storage-purge';
import { AccessTokenHolder } from './access-token-holder.service';
import { AuthSyncService } from './auth-sync.service';

const REFRESH_LOCK = 'galactus.auth.refresh';

/**
 * Thrown by {@link AuthRefreshService} when a refresh came back as a DIFFERENT user and the rescue
 * ran. <b>It does NOT mean the refresh failed</b> — the incoming session is fully applied (bearer,
 * store, cross-tab broadcast) by the time it is thrown. It exists to stop the CALLER from carrying
 * on with a token that no longer belongs to whoever composed its request: `handle401` replays the
 * original request with the token `refresh()` hands back, so resolving would send the outgoing
 * user's pending mutation out under the incoming user's bearer, to be authorized and recorded as
 * them.
 *
 * <p>Every consumer of {@code refresh()} must therefore tell this apart from a genuine failure:
 * tearing the session down here would sign the user who just arrived straight back out. The one
 * consumer that can see it is {@code SharedHttpInterceptor.handle401} (the bootstrap path cannot —
 * a tab with no identity yet never classifies as a switch), and its carve-out is spec'd.
 */
export class IdentitySwitchedError extends Error {
  constructor() {
    super('the refreshed token belongs to a different user; the caller must not continue');
    // Restore the prototype chain: TypeScript's ES5 target breaks `instanceof` on Error subclasses
    // otherwise, and the interceptor's carve-out is an instanceof check.
    Object.setPrototypeOf(this, IdentitySwitchedError.prototype);
    this.name = 'IdentitySwitchedError';
  }
}

/**
 * Owns silent access-token renewal via the httpOnly refresh cookie.
 * In-tab: concurrent callers share ONE in-flight request. Cross-tab: a Web Lock
 * serializes refreshes so two tabs don't race the rotating cookie.
 */
@Injectable({ providedIn: 'root' })
export class AuthRefreshService {
  private readonly url = `${environment.apiUrl}/v2/refresh`;
  private readonly logoutUrl = `${environment.apiUrl}/v2/logout`;
  /**
   * Single-flight cache: a Promise memoizes the in-flight refresh so concurrent callers
   * share one HTTP request. We use a Promise (not an Observable+shareReplay) because the
   * earlier shareReplay({refCount:true})+finalize variant had a teardown race -- caller A's
   * take(1) tearing down its subscription dropped refCount to 0, finalize cleared the
   * cache, and a caller B subscribing right after spawned a second /v2/refresh. With the
   * second refresh racing the cookie rotation from the first, reuse-detection on the
   * backend could revoke the entire family. A Promise locks its settled value: every
   * caller that called refresh() before the .finally reset fires sees the same resolved
   * token; the reset itself is queued so synchronous chained callers in the same tick
   * still share. New callers AFTER the reset get a fresh request.
   */
  private inFlight?: Promise<string>;

  constructor(private http: HttpClient, private store: Store<State>, private authSync: AuthSyncService, private zone: NgZone, private tokenHolder: AccessTokenHolder, private push: PushNotificationService,
    private offlineMode: OfflineModeState,
    @Inject(OFFLINE_SESSION_ENABLED) private offlineSessionEnabled: boolean,
    private location: Location) {}

  /** Returns the new access token; de-duplicates concurrent calls within this tab. */
  refresh(): Observable<string> {
    if (!this.inFlight) {
      const promise = this.withCrossTabLock(() => this.requestNewToken())
        .then((token) => this.applyToken(token));
      this.inFlight = promise;
      // Reset on terminal settlement so a LATER refresh starts a NEW HTTP request. Use
      // then(reset, reset) instead of .finally(reset) so the rejection branch of
      // `promise` is observed by an explicit handler -- otherwise the cleanup chain
      // would leave the rejection unhandled (.finally doesn't swallow errors). Callers
      // who already subscribed to from(this.inFlight) see the same resolved/rejected
      // promise; the reset only invalidates the field for FUTURE refresh() calls.
      const cleanup = () => { this.inFlight = undefined; };
      promise.then(cleanup, cleanup);
    }
    return from(this.inFlight);
  }

  /**
   * App-init bootstrap: try once to obtain a token from an existing cookie; never reject.
   * Wipes any auth slice rehydrated from localStorage (the metareducer persists state-tree
   * snapshots and we don't want stale identity / leftover token fields surviving a reload).
   * After this dispatch the bootstrap either refills via refresh (success) or leaves the slice
   * empty (failure) -- either way no stale data sticks.
   */
  bootstrap(): Promise<boolean> {
    // One-shot migration: wipe pre-refactor auth blobs from local/session storage + cookies.
    // Idempotent, runs once per boot, must happen BEFORE refresh so a stale legacy JWT
    // can't be picked up by any downstream code that still reads the old keys (none today,
    // but the guarantee is "by the time anything else runs, the old keys are gone").
    purgeLegacyAuthStorage();
    // Wipe BOTH session slices via the one atomic session action — identity AND the forced-2FA
    // enrollment flag — mirroring every other apply/teardown site (login, refresh, cross-tab sync,
    // clearSession). The legacy AuthActionType.FULL used here before reset only the auth slice, leaving
    // the twoFactor slice untouched: benign today (it starts false and the metareducer strips any
    // rehydrated value), but the lone write path that didn't honour the single-writer invariant — so a
    // stale enrollmentRequired could survive the wipe if that stripping ever regressed.
    this.store.dispatch(new BaseAction(SessionActionType.APPLY_SESSION, { auth: EMPTY_AUTH, enrollmentRequired: false }));
    // A logout made offline never reached the server, so the refresh cookie is still alive: the
    // refresh below would sign the user who logged out straight back in. Finish that logout instead.
    if (isLogoutPending()) {
      return this.completePendingLogout();
    }
    return lastValueFrom(
      this.refresh().pipe(
        map(() => true),
        catchError((e) => {
          this.decideOfflineBoot(e);
          return of(true);
        }),
      ),
    );
  }

  /**
   * The boot refresh failed: decide between the login screen (as always) and OFFLINE MODE.
   *
   * Offline mode only when ALL of: the feature is on, the failure is NOT the server refusing the
   * cookie (`isAuthRejection`: 401/403), the BROWSER reports no network (`navigator.onLine ===
   * false`, product owner 25/10/2026: "offline solo se davvero senza rete" -- a status 0 on a working
   * network is a gateway restart or a deploy, and offline mode there put online users on Scan and
   * lost their deep link), and a remembered identity is still within its 8 h validity and may open
   * the catalogue at all. Anything else is today's behaviour: the slice stays `EMPTY_AUTH` and the
   * guards route to /auth, capturing the return URL. ONLY a 401/403 FORGETS the identity -- the
   * cookie it stood for is gone, so it must not be offered again on the next offline boot; a status
   * 0, a 5xx (a deploy) or a parse error (a captive portal answering 200 with HTML) keeps it: they
   * say nothing about the cookie (review 25/10/2026).
   *
   * The URL the boot asked for travels with the entry when offline mode cannot serve it (the guards
   * send the tab to the offline home): `OfflineSessionService` navigates back to it on reconnect. An
   * offline route is not recorded -- the guards admit it, the tab stays on it.
   *
   * Entering dispatches nothing (see `OfflineModeState`): `APPLY_SESSION` stays the single writer of
   * the session slices, and the store keeps saying "no session" for the whole of offline mode.
   */
  private decideOfflineBoot(error: unknown): void {
    if (!this.offlineSessionEnabled) {
      return;
    }
    if (isAuthRejection(error)) {
      forgetOfflineIdentity();
      return;
    }
    if (!browserReportsOffline()) {
      return;
    }
    const identity = readOfflineIdentity();
    if (identity && canUseOfflineCatalog(identity.roles)) {
      // Read off the address bar: this runs inside APP_INITIALIZER, before the first navigation.
      const requested = this.location.path(true);
      const returnUrl =
        isRestorableAppUrl(requested) && !isOfflineAllowedFor(identity.roles, requested) ? requested : null;
      this.offlineMode.enter(identity, returnUrl);
    }
  }

  /**
   * Boot with a logout still owed to the server (`markLogoutPending`): post /v2/logout in place of
   * the refresh, so the cookie is revoked rather than renewed. The tab boots signed out either way
   * -- the slice stays `EMPTY_AUTH`, no offline mode (the teardown already forgot the identity).
   * The record is dropped once the server answered: a 2xx revoked the family, a 401/403 says the
   * cookie was already dead. With no network, or a server that did not answer (status 0, a 5xx
   * during a deploy), it is kept for the next boot. Never rejects, like {@link bootstrap}.
   */
  private completePendingLogout(): Promise<boolean> {
    if (browserReportsOffline()) {
      return Promise.resolve(true);
    }
    return lastValueFrom(
      this.http.post(this.logoutUrl, {}, { withCredentials: true }).pipe(
        map(() => {
          clearLogoutPending();
          return true;
        }),
        catchError((e) => {
          if (isAuthRejection(e)) {
            clearLogoutPending();
          }
          return of(true);
        }),
      ),
    );
  }

  private requestNewToken(): Promise<string> {
    return lastValueFrom(
      this.http.post<{ token: string; type: string }>(this.url, {}, { withCredentials: true }).pipe(
        map((res) => res['token'])
      )
    );
  }

  private applyToken(token: string): string {
    // Decode the refreshed token ONCE for both the identity slice and the enrollment flag — and do it
    // BEFORE swapping the in-memory bearer (mirrors AuthService.applyToken). If decodeAuthToken throws on
    // a malformed /v2/refresh response, the holder must still carry the PREVIOUS token rather than a token
    // we could not validate; the refresh promise then rejects (the interceptor bounces to login) with the
    // holder left in a consistent state instead of holding an unusable bearer.
    const { auth, enrollment: enrollmentRequired } = decodeAuthToken(token);
    // A silent refresh can hand THIS tab to a different user: another tab signed in as B, rotating the
    // shared refresh cookie, and the next renewal here comes back as B while A's screen — and any
    // dialog opened against A's record — is still in front of the user. Sibling tabs learn that from
    // the notifyLogin below and run the rescue in their handler, but BroadcastChannel does not echo to
    // the sender, so without this the tab that actually did the refresh is the ONE left stranded on
    // the outgoing user's page. Classified BEFORE the dispatch, which overwrites the identity read.
    const identity = this.authSync.classifyIdentity(auth);
    if (identity === 'switch') {
      // Overlays first, mirroring the broadcast path — NOT because the ordering protects a dialog
      // handler (it can't: `afterClosed` fires at the END of the exit animation, always after the
      // synchronous bearer swap below; `wasForceClosed` is what stops those handlers) but so the
      // outgoing user's dialogs are already going away while the rest of the rescue runs, and so the
      // two sites read the same. Only the overlay close and the navigation are shared with the
      // broadcast path:
      // that path ALSO clears the bearer, which here would be exactly wrong — the holder is about to
      // be handed the incoming user's own valid token, and clearing it would just force a redundant
      // 401 + refresh round trip.
      this.authSync.closeOverlaysForIdentitySwitch();
    }
    // Bearer goes to the in-memory holder; only the non-secret identity slice enters the store.
    this.tokenHolder.set(buildAuthToken(token));
    // Re-establish (or clear) the forced-2FA-enrollment flag from the refreshed token. enrollmentRequired
    // lives only in the in-memory store and is wiped on a hard refresh; the login path sets it from the
    // response body, but the bootstrap/refresh path only has the token. Without this, a refresh during
    // forced enrollment would leave enrollmentRequired=false and the guard would admit the user to /app.
    // Apply the identity and the flag ATOMICALLY (one action, both reducers) and broadcast it so sibling
    // tabs stay consistent.
    this.store.dispatch(new BaseAction(SessionActionType.APPLY_SESSION, { auth, enrollmentRequired }));
    this.authSync.notifyLogin(auth, enrollmentRequired);
    // Every successful refresh re-dates the offline identity (8 h from now, the cookie's idle
    // lifetime), so it slides exactly as the cookie does. An enrollment-scoped token clears it.
    if (this.offlineSessionEnabled) {
      rememberOfflineIdentity(auth, enrollmentRequired);
    }
    if (identity === 'switch') {
      // AFTER the dispatch, so the destination's guards and landing fetch resolve under the new
      // identity. Handles EVERY destination a switch can have — /app, the enrollment wizard (whose
      // route step shows the outgoing user's one-time backup codes), a parked /auth screen — because
      // this path reaches all of them too: a wizard tab's own /v2/profile/2fa 401 goes down this
      // refresh path (the interceptor's enrollment backstop deliberately exempts those endpoints), so
      // a per-path subset of the rescue would strand exactly the screen that must not be stranded.
      this.authSync.leaveAfterSwitch(enrollmentRequired);
      // The stored subscription still maps to the OUTGOING user, so their notifications would land
      // on the incoming user's device. Silent: browser permission is scoped to the origin, not the
      // user, so an already-granted device needs no gesture — and if permission is not granted this
      // does nothing and NO PROMPT APPEARS.
      this.push.refreshForCurrentIdentity(enrollmentRequired);
      // ABORT THE CALLER, having applied everything above. handle401 replays the original request
      // with the token this method returns — and that request was composed by, and for, the OUTGOING
      // user. Handing the token back would send their pending mutation out under the INCOMING user's
      // bearer, which the backend authorizes and records as the incoming user: the wrong-user
      // attribution this whole rescue exists to prevent, and deterministic here rather than the
      // already-on-the-wire race the broadcast path accepts. The caller sees the request fail
      // unauthorized, which is exactly what happened from its point of view.
      throw new IdentitySwitchedError();
    }
    return token;
  }

  /**
   * Serialize refreshes across tabs. Overridable in tests / browsers without Web Locks.
   * The navigator.locks callback is NOT patched by zone.js, so it runs in the root
   * zone; we re-enter NgZone for `fn` so the refresh continuation (store dispatch +
   * change detection on success, or interceptor navigation on failure) runs inside
   * Angular's zone — otherwise change detection silently misses and router navigation
   * warns "Navigation triggered outside Angular zone".
   */
  protected withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = (navigator as any)['locks'];
    if (locks && typeof locks['request'] === 'function') {
      return locks['request'](REFRESH_LOCK, () => this.zone.run(fn));
    }
    return this.zone.run(fn);
  }
}
